import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect } from "react";
import { API_URL, resolveMediaUrl } from "../lib/api";
import { lockScrollToTop } from "../lib/scroll";

const PROJECT_DETAIL_REVEAL_OFFSET_PX = 25;
const reveal = { initial: { opacity: 0, y: PROJECT_DETAIL_REVEAL_OFFSET_PX }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.15 } };

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { data: project, isLoading, isError } = useQuery({ queryKey: ["public-project", slug], queryFn: async () => { const response = await fetch(`${API_URL}/projects/${slug}`); if (!response.ok) throw new Error("Project not found"); return response.json(); } });
  useLayoutEffect(() => project ? lockScrollToTop() : undefined, [project, slug]);
  if (isLoading) return <main className="project-state" data-testid="project-loading">Loading project…</main>;
  if (isError || !project) return <main className="project-state" data-testid="project-not-found"><h1>Project not found.</h1><Link to="/">Return home</Link></main>;
  const titleParts = project.title.split("—");
  return (
    <main className="v2-public v2-inner-page" data-testid="project-detail-page">
      <section className="v2-project-detail-hero" data-testid="project-detail-hero">
        <Link to="/projects" className="v2-back-link" data-testid="project-back-link"><ArrowLeft /> Back to case studies</Link>
        <span className="v2-overline">Completed project</span>
        <h1 data-testid="project-title">{titleParts[0].trim()}<br /><em>{titleParts[1]?.trim() || project.location}.</em></h1>
        <div className="v2-project-facts" data-testid="project-facts">
          <div><small>Location</small><strong>{project.location}</strong></div>
          <div><small>Client</small><strong>{project.client_name}</strong></div>
          <div><small>Discipline</small><strong>{project.discipline}</strong></div>
        </div>
      </section>
      <section className="v2-project-cover" data-testid="project-cover-image"><img src={resolveMediaUrl(project.cover_image)} alt={`${project.title} mechanical project`} /></section>
      <section className="v2-project-intro" data-testid="project-scope-section">
        <div><span className="v2-overline">The brief</span><h2 data-testid="project-scope-heading">Built for performance.<br /><em>Finished with care.</em></h2></div>
        <div><p data-testid="project-scope-text">{project.summary}</p><ul>{project.stages.map((stage) => <li key={stage}><Check />{stage}</li>)}</ul></div>
      </section>

      <section className="v2-project-gallery" data-testid="project-gallery">
        {project.gallery.map((image, index) => (
          <motion.figure className={`v2-gallery-item v2-gallery-item-${index + 1}`} key={image.src} {...reveal} data-testid={`project-gallery-image-${index + 1}`}>
            <img src={resolveMediaUrl(image.src)} alt={image.alt} />
            <figcaption><span>0{index + 1}</span>{image.caption}</figcaption>
          </motion.figure>
        ))}
      </section>

      <section className="v2-inline-cta" data-testid="project-completion-section">
        <div><span className="v2-overline">Project delivered</span><h2 data-testid="project-completion-heading">A complete system. A clean finish.</h2></div>
        <Link to="/contact" className="v2-button-primary" data-testid="project-contact-link">Discuss your project <ArrowRight /></Link>
      </section>
    </main>
  );
}